// src/hooks/useLeadDerivedFields.ts
import { useMemo } from "react";

import type { ExtractedLeadV2 } from "../types/lead";

type Inputs = {
  currentLeadName: string;
  e: ExtractedLeadV2;
  locationName?: string | null;
  locationAddress?: string | null;
  leadId?: string | number | null;
  leadLastUpdated?: number;
  isTest?: boolean;
  sent?: boolean;
  queued?: boolean;
  nowMs?: number;
};

export function formatLeadAge(ms: number): string {
  if (ms < 60_000) return "under 1 minute";

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) {
    return `${minutes} min${minutes === 1 ? "" : "s"}`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remMinutes = minutes % 60;
    if (remMinutes === 0) {
      return `${hours} hr${hours === 1 ? "" : "s"}`;
    }
    return `${hours} hr${hours === 1 ? "" : "s"} ${remMinutes} min`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    const remHours = hours % 24;
    if (remHours === 0) {
      return `${days} day${days === 1 ? "" : "s"}`;
    }
    return `${days} day${days === 1 ? "" : "s"} ${remHours} hr${remHours === 1 ? "" : "s"}`;
  }

  const weeks = Math.floor(days / 7);
  if (weeks < 4) {
    const remDays = days % 7;
    if (remDays === 0) {
      return `${weeks} week${weeks === 1 ? "" : "s"}`;
    }
    return `${weeks} week${weeks === 1 ? "" : "s"} ${remDays} day${remDays === 1 ? "" : "s"}`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    const remDays = days % 30;
    if (remDays === 0) {
      return `${months} month${months === 1 ? "" : "s"}`;
    }
    return `${months} month${months === 1 ? "" : "s"} ${remDays} day${remDays === 1 ? "" : "s"}`;
  }

  const years = Math.floor(days / 365);
  const remDays = days % 365;
  if (remDays === 0) {
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  return `${years} year${years === 1 ? "" : "s"} ${remDays} day${remDays === 1 ? "" : "s"}`;
}

export function useLeadDerivedFields(inputs: Inputs) {
  const {
    currentLeadName: _currentLeadName,
    e,
    locationName,
    locationAddress,
    leadId,
    leadLastUpdated,
    isTest,
    sent,
    queued,
    nowMs,
  } = inputs;

  return useMemo(() => {
    const leadLastUpdatedSafe = leadLastUpdated ?? 0;
    const isTestSafe = !!isTest;
    const sentSafe = !!sent;
    const queuedSafe = !!queued;
    // eslint-disable-next-line react-hooks/purity
    const now = nowMs ?? Date.now();

    const locationDisplay = locationName
      ? locationAddress
        ? `${locationName} • ${locationAddress}`
        : locationName
      : "—";

    const installAddressDisplay =
      e.install_address_full || e.customer_city_state || "—";

    const leadSourceDisplay = e.lead_source_category
      ? e.lead_source_detail
        ? `${e.lead_source_category} (${e.lead_source_detail})`
        : e.lead_source_category
      : "—";

    const timelineDisplay = e.ready_to_buy_timeline || "—";

    const createdAtMs = Number(leadId) || leadLastUpdatedSafe;
    const ageMs = now - createdAtMs;
    const leadAgeDisplay = formatLeadAge(ageMs);

    const leadStatusLabel = isTestSafe
      ? "TEST"
      : sentSafe
      ? "Sent"
      : queuedSafe
      ? "Queued"
      : "Draft";

    const leadStatusClass =
      "text-xs px-2 py-1 rounded-full " +
      (isTestSafe
        ? "bg-yellow-100 text-yellow-800"
        : sentSafe
        ? "bg-green-100 text-green-800"
        : queuedSafe
        ? "bg-blue-100 text-blue-800"
        : "bg-gray-200 text-gray-700");

    return {
      locationDisplay,
      leadSourceDisplay,
      timelineDisplay,
      installAddressDisplay,
      leadStatusClass,
      leadStatusLabel,
      leadAgeDisplay,
    };
  }, [
    e,
    locationName,
    locationAddress,
    leadId,
    leadLastUpdated,
    isTest,
    sent,
    queued,
    nowMs,
  ]);
}

export type DerivedBundle = ReturnType<typeof useLeadDerivedFields>;
